/**
 * A PDF no source-list row claimed: what it says it is, the rows it most resembles
 * (click to match), and the ways out — select it and pick a row, keep it as its own
 * source, or discard it.
 */
import React, { useState } from 'react';
import styled from '@emotion/styled';
import { FileText, Trash2, Plus } from 'lucide-react';
import { Input, Button, Actions } from '../../../components/InstanceSettings/formPrimitives';
import API_ENDPOINTS from '../../../config/api';
import { useTranslation } from '../../../hooks/useTranslation.jsx';
import { pdfTitle, pdfPublisher } from './importApi';
import { Meta } from './styles';

export const PairCard = styled.div`
  padding: 12px 14px;
  margin-bottom: 8px;
  border-radius: 10px;
  border: 1px solid ${(p) => (p.$selected ? '#c084fc' : 'rgba(255, 255, 255, 0.1)')};
  background: ${(p) => (p.$selected ? 'rgba(147, 51, 234, 0.12)' : 'rgba(255, 255, 255, 0.03)')};
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  color: #ddd;
`;

export const CardHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #fff;
  word-break: break-word;
`;

const Suggestion = styled.button`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  text-align: left;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  color: #ddd;
  font-size: 12px;
  cursor: pointer;
  &:hover { border-color: #c084fc; color: #fff; }
`;

const Excerpt = styled.details`
  font-size: 12px;
  color: #999;
  summary { cursor: pointer; }
  pre { white-space: pre-wrap; margin: 6px 0 0; max-height: 160px; overflow: auto; }
`;

export default function UnmatchedPdfCard({ datasetId, pdf, rowsById, selected, busy, onSelect, onMatch, onKeep, onDiscard }) {
  const { t } = useTranslation();
  const [keeping, setKeeping] = useState(false);
  const [title, setTitle] = useState(pdfTitle(pdf.pdf) || '');
  const [publisher, setPublisher] = useState(pdfPublisher(pdf.pdf) || '');
  const suggestions = (pdf.candidates || []).filter((c) => rowsById[c.rowId]);
  const readTitle = pdfTitle(pdf.pdf);

  return (
    <PairCard $selected={selected}>
      <CardHead>
        <FileText size={14} />
        {/* Opens the stored PDF itself (#973): a presigned link straight from the bucket. */}
        <a href={API_ENDPOINTS.ADMIN_DATASET_SOURCE_FILE(datasetId, pdf.id)} target="_blank" rel="noreferrer"
          title={t('datasetWorkspace.openPdf')} style={{ color: 'inherit' }}>
          {pdf.filename}
        </a>
      </CardHead>
      <Meta>
        <span>{t('datasetImport.readAs')}: {readTitle || t('datasetImport.unknownTitle')}</span>
        {pdfPublisher(pdf.pdf) && <span>· {pdfPublisher(pdf.pdf)}</span>}
      </Meta>
      {pdf.pdf?.error && <Meta style={{ color: '#f87171' }}>{pdf.pdf.error}</Meta>}
      {pdf.pdf?.excerpt && (
        <Excerpt>
          <summary>{t('datasetImport.excerpt')}</summary>
          <pre>{pdf.pdf.excerpt}</pre>
        </Excerpt>
      )}

      {suggestions.length > 0 && (
        <>
          <Meta>{t('datasetImport.suggestions')}</Meta>
          {suggestions.map((c) => (
            <Suggestion key={c.rowId} type="button" disabled={busy} onClick={() => onMatch(pdf.id, c.rowId)}>
              <span>{rowsById[c.rowId].title}</span>
              <span style={{ color: '#888', whiteSpace: 'nowrap' }}>
                {Math.round(c.score * 100)} % · {t(`datasetImport.methods.${c.method}`)}
              </span>
            </Suggestion>
          ))}
        </>
      )}

      {keeping ? (
        <>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('datasetWorkspace.sourceName')} />
          <Input value={publisher} onChange={(e) => setPublisher(e.target.value)} placeholder={t('datasetWorkspace.publisher')} />
          <Actions>
            <Button $size="small" $variant="primary" disabled={busy || !title.trim()} onClick={() => onKeep(pdf.id, { title, publisher })}>
              {t('datasetImport.keep')}
            </Button>
            <Button $size="small" $variant="ghost" onClick={() => setKeeping(false)}>{t('datasetWorkspace.cancel')}</Button>
          </Actions>
        </>
      ) : (
        <Actions>
          <Button $size="small" $variant={selected ? 'primary' : undefined} onClick={() => onSelect(selected ? null : pdf.id)}>
            {selected ? t('datasetImport.selected') : t('datasetImport.selectPdf')}
          </Button>
          <Button $size="small" $variant="ghost" onClick={() => setKeeping(true)}><Plus /> {t('datasetImport.keepOwn')}</Button>
          <Button $size="small" $variant="ghost" $tone="danger" disabled={busy} onClick={() => onDiscard(pdf.id)}>
            <Trash2 /> {t('datasetImport.discard')}
          </Button>
        </Actions>
      )}
    </PairCard>
  );
}
